/** 旧模式中的订单操作列为字符串格式  */
import ShopType from "@/enum/ShopType";
import { ShippingDeadlineShopTypes, shouldShowDropShippingDetail, SupplierShopTypes } from "@/utils/ShopUtils";
import GlobalStore from "@/store/index";
import OrderTableColumnsType from "../../../enum/OrderTableColumnsType";
import ShopDTO from "@/dto/shop/ShopDTO";
import { xor } from "lodash";

const ORDER_TABLE_COLUMNS_KEY_PREFIX = "__ORDER_TABLE_COLUMNS_KEY_PREFIX__";
const ORDER_TABLE_COLUMNS_ALL_SHOP_ORDER_KEY = "ALL_SHOP_KEY";
/** 如果已经操作过订单列，存在本地cache，成为代理商后不会默认将代发信息列显示出来，需要下面的Flag标识 */
const OPERATED_COLUMNS_AFTER_BECOMING_RETAILER_PREFIX = "__OPERATED_COLUMNS_AFTER_BECOMING_RETAILER_PREFIX__";
const supportInvoiceShopTypes = [ShopType.JD, ShopType.SUNING, ShopType.YOUZAN];

function buildKey(shopId: number | undefined) {
    if (shopId) {
        return `${ORDER_TABLE_COLUMNS_KEY_PREFIX}${shopId}`;
    } else {
        return `${ORDER_TABLE_COLUMNS_KEY_PREFIX}${ORDER_TABLE_COLUMNS_ALL_SHOP_ORDER_KEY}`;
    }
}

export interface OrderTableColumn {
    /** 操作的序号 */
    index: number;
    type: OrderTableColumnsType;
    /** 是否在操作栏中显示 */
    visible: boolean;
    /** 是否禁止操作显示 */
    disabled?: boolean;
}

/** 固定列 */
export const UnOperationOrderTableColumnsType = [
    OrderTableColumnsType.CHECKBOX,
    OrderTableColumnsType.STATUS,
    OrderTableColumnsType.OPERATION,
];

/** 可以移动，不能关闭的列 */
export const fixedVisibleColumns = [OrderTableColumnsType.CONSIGNEE, OrderTableColumnsType.WAYBILL_NO];

/** 默认为可视的列 */
const defaultVisibleColumnsType = [
    OrderTableColumnsType.CHECKBOX,
    OrderTableColumnsType.SHOP_INFO,
    OrderTableColumnsType.STATUS,
    OrderTableColumnsType.RETAILER,
    OrderTableColumnsType.BUYER_USERNAME,
    OrderTableColumnsType.QUANTITY,
    OrderTableColumnsType.PAYMENTS,
    OrderTableColumnsType.SELLER_RECEIPTS,
    OrderTableColumnsType.PRODUCT,
    OrderTableColumnsType.CONSIGNEE,
    OrderTableColumnsType.WAYBILL_NO,
    OrderTableColumnsType.OPERATION,
    OrderTableColumnsType.SUPPLIER_DROPSHIPPING2,
];

/**
 * 整店打单默认
 */
function getAllOrderShopColumnTypes() {
    return [
        OrderTableColumnsType.CHECKBOX,
        OrderTableColumnsType.STATUS,
        OrderTableColumnsType.SHOP_INFO,
        OrderTableColumnsType.ORIGINAL_ID,
        GlobalStore.companyRoleStore.isSupplier ? OrderTableColumnsType.RETAILER : undefined,
        OrderTableColumnsType.BUYER_USERNAME,
        OrderTableColumnsType.QUANTITY,
        OrderTableColumnsType.PAYMENTS,
        OrderTableColumnsType.SELLER_RECEIPTS,
        OrderTableColumnsType.PRODUCT,
        OrderTableColumnsType.CUSTOMIZATION_INFO,
        OrderTableColumnsType.ORDER_TIME,
        OrderTableColumnsType.SHIPPING_DEADLINE,
        OrderTableColumnsType.PAYMENT_TIME,
        OrderTableColumnsType.CONSIGNEE,
        OrderTableColumnsType.ORDER_REMARK,
        OrderTableColumnsType.SHIPPING_TIME,
        OrderTableColumnsType.WAYBILL_NO,
        GlobalStore.companyRoleStore.isRetailer ? OrderTableColumnsType.SUPPLIER_DROPSHIPPING2 : undefined,
        OrderTableColumnsType.SEQUENCE_NO,
        OrderTableColumnsType.WEIGHT,
        OrderTableColumnsType.OPERATION,
    ].filter(Boolean);
}

function getOneShopColumnTypes(shop: ShopDTO) {
    return [
        OrderTableColumnsType.CHECKBOX,
        OrderTableColumnsType.STATUS,
        OrderTableColumnsType.ORIGINAL_ID,
        SupplierShopTypes.includes(shop.type) ? OrderTableColumnsType.RETAILER : undefined,
        OrderTableColumnsType.BUYER_USERNAME,
        OrderTableColumnsType.QUANTITY,
        OrderTableColumnsType.PAYMENTS,
        [ShopType.PINDUODUO, ShopType.DOUDIAN, ShopType.XIAOHONGSHU, ShopType.CHANNEL_SHOP].includes(shop.type)
            ? OrderTableColumnsType.SELLER_RECEIPTS
            : undefined,
        OrderTableColumnsType.PRODUCT,
        shop.type !== ShopType.ALIBABA ? OrderTableColumnsType.CUSTOMIZATION_INFO : undefined,
        OrderTableColumnsType.ORDER_TIME,
        ShippingDeadlineShopTypes.includes(shop.type) ? OrderTableColumnsType.SHIPPING_DEADLINE : undefined,
        OrderTableColumnsType.PAYMENT_TIME,
        supportInvoiceShopTypes.includes(shop.type) ? OrderTableColumnsType.INVOICE_INFO : undefined,
        OrderTableColumnsType.CONSIGNEE,
        OrderTableColumnsType.ORDER_REMARK,
        OrderTableColumnsType.SHIPPING_TIME,
        OrderTableColumnsType.WAYBILL_NO,
        shouldShowDropShippingDetail(shop) && GlobalStore.companyRoleStore.isRetailer
            ? OrderTableColumnsType.SUPPLIER_DROPSHIPPING2
            : undefined,
        OrderTableColumnsType.SEQUENCE_NO,
        OrderTableColumnsType.WEIGHT,
        [ShopType.MANUAL, ShopType.INDEPENDENT_MANUAL].includes(shop.type) ? OrderTableColumnsType.SHIPPER : undefined,
        [ShopType.DOUDIAN, ShopType.DOUDIAN_IOP, ShopType.DOUDIAN_SUPPLIER].includes(shop.type)
            ? OrderTableColumnsType.LOGISTICS_AGING_PREDICT
            : undefined,
        [ShopType.KWAI].includes(shop.type) ? OrderTableColumnsType.LOGISTICS_RECOMMEND : undefined,
        OrderTableColumnsType.QUICK_OPERATION,
        OrderTableColumnsType.OPERATION,
    ].filter((it) => !!it);
}

function getColumnTypes(shop: ShopDTO | undefined) {
    if (!shop) {
        return getAllOrderShopColumnTypes();
    }

    return getOneShopColumnTypes(shop);
}

export function getOrderTableColumnsStorage(shop: ShopDTO | undefined): OrderTableColumn[] {
    const shopId = shop?.id;

    const cacheKey = buildKey(shopId);

    const orderTableColumnsStr = localStorage.getItem(cacheKey);

    if (orderTableColumnsStr) {
        try {
            let orderTableColumns: OrderTableColumn[] = JSON.parse(orderTableColumnsStr);

            const allColumnsTypes = getColumnTypes(shop);
            const hasDifferenceColumns = xor(
                allColumnsTypes,
                orderTableColumns.map((it) => it.type),
            ).length;

            /**
             * 列数发生变化时，直接简单处理为重置缓存,优先已最新的列信息为准
             */
            if (hasDifferenceColumns) {
                const newOrderTableColumns: OrderTableColumn[] = allColumnsTypes.map((type, index) => {
                    const column = orderTableColumns.find((it) => it.type === type);

                    if (column) {
                        return {
                            ...column,
                            index,
                            type,
                        };
                    } else {
                        return {
                            index,
                            type,
                            visible: false,
                            disabled: fixedVisibleColumns.includes(type),
                        };
                    }
                });

                orderTableColumns = newOrderTableColumns;

                setOrderTableColumnsStorage(shop, orderTableColumns);
            }

            return orderTableColumns;
        } catch (error) {
            console.error("解析orderTableColumns失败", error);
        }
    }

    return getDefaultOrderTableColumns(shop);
}

/**
 * 保存订单表格显示列信息
 */
export function setOrderTableColumnsStorage(shop: ShopDTO | undefined, orderTableColumns) {
    const shopId = shop?.id;
    const cacheKey = buildKey(shopId);

    const orderTableColumnsStr = JSON.stringify(orderTableColumns);

    const orderTableColumnsKey = cacheKey;

    if (!shopId) {
        localStorage.setItem(orderTableColumnsKey, orderTableColumnsStr);

        return;
    }

    const isRetailer = GlobalStore.companyRoleStore.isRetailer;

    if (isRetailer) {
        const retailerOperatedColumnsKey = OPERATED_COLUMNS_AFTER_BECOMING_RETAILER_PREFIX + shopId;

        localStorage.setItem(retailerOperatedColumnsKey, "1");
    }

    localStorage.setItem(orderTableColumnsKey, orderTableColumnsStr);
}

export function getMenuDisplayColumns(shop: ShopDTO | undefined) {
    const cacheOrderTableColumns = getOrderTableColumnsStorage(shop);

    const operableOrderTableColumns = cacheOrderTableColumns.filter(
        ({ type: key }) => !UnOperationOrderTableColumnsType.includes(key),
    );

    return operableOrderTableColumns;
}

export function getDefaultOrderTableColumns(shop: ShopDTO): OrderTableColumn[] {
    const columns = getColumnTypes(shop);

    const orderTableColumns = columns.map((type, index) => {
        const visible = defaultVisibleColumnsType.includes(type);

        const disabled = fixedVisibleColumns.includes(type);

        return {
            type,
            index,
            visible,
            disabled,
        };
    });

    return orderTableColumns;
}
